import { useEffect, useState } from "react";
import { Download, Users, CalendarCheck, CheckCircle2, UserX, XCircle, Percent, PoundSterling, Timer, Briefcase } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, API } from "../../lib/api";

export default function ReportsTab({ restaurant }) {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [summary, setSummary] = useState(null);
  const [exec, setExec] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/restaurants/${restaurant.id}/reports/summary`, { params: { from_date: from, to_date: to } })
      .then((r) => { if (live) setSummary(r.data); })
      .catch(() => {});
    api.get(`/restaurants/${restaurant.id}/reports/exec`, { params: { from_date: from, to_date: to } })
      .then((r) => { if (live) setExec(r.data); })
      .catch(() => {});
    return () => { live = false; };
  }, [restaurant.id, from, to]);

  const downloadCsv = () => {
    window.open(`${API}/restaurants/${restaurant.id}/reports/bookings.csv?from_date=${from}&to_date=${to}`, "_blank");
  };

  const cards = summary ? [
    { icon: CalendarCheck, label: "Bookings", value: summary.bookings, testid: "rep-bookings" },
    { icon: Users, label: "Covers", value: summary.covers, testid: "rep-covers" },
    { icon: CheckCircle2, label: "Completed", value: summary.completed, testid: "rep-completed" },
    { icon: UserX, label: "No-shows", value: summary.no_shows, testid: "rep-noshows" },
    { icon: Percent, label: "No-show rate", value: `${summary.no_show_rate}%`, testid: "rep-noshow-rate" },
    { icon: XCircle, label: "Cancelled", value: summary.cancelled, testid: "rep-cancelled" },
  ] : [];

  return (
    <div data-testid="reports-tab">
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">From</Label>
          <Input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="mt-1.5 bg-card border-border/60 max-w-[180px]" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">To</Label>
          <Input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="mt-1.5 bg-card border-border/60 max-w-[180px]" />
        </div>
        <Button data-testid="download-csv-btn" onClick={downloadCsv} className="rounded-full">
          <Download className="w-4 h-4 mr-2" /> Download CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid}
            className="rounded-2xl border border-border/60 bg-card p-5 hover:-translate-y-[1px] transition-transform duration-200">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{c.label}</p>
              <c.icon className="w-4 h-4 text-primary" />
            </div>
            <p className="font-outfit font-semibold text-3xl mt-3">{c.value}</p>
          </div>
        ))}
      </div>
      {exec && (
        <div className="mt-8" data-testid="exec-section">
          <h3 className="font-outfit font-medium text-lg mb-4">Sales & service</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {[
              { icon: PoundSterling, label: "Sales (paid orders)", value: `£${exec.sales.toFixed(2)}`, testid: "exec-sales" },
              { icon: Users, label: "Avg spend / cover", value: `£${exec.avg_spend_per_cover.toFixed(2)}`, testid: "exec-avg-spend" },
              { icon: Timer, label: "Avg table turn", value: exec.avg_turn_minutes != null ? `${exec.avg_turn_minutes}m` : "–", testid: "exec-turn" },
              { icon: Briefcase, label: "Labor cost", value: `£${exec.labor_cost.toFixed(2)}`, testid: "exec-labor" },
              { icon: Percent, label: "Labor / sales", value: exec.sales ? `${exec.labor_pct}%` : "–", testid: "exec-labor-pct" },
            ].map((c) => (
              <div key={c.label} data-testid={c.testid}
                className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5 hover:-translate-y-[1px] transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{c.label}</p>
                  <c.icon className="w-4 h-4 text-primary" />
                </div>
                <p className="font-outfit font-semibold text-2xl sm:text-3xl mt-3">{c.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {exec?.turn_by_service?.length > 0 && (
        <div className="mt-6" data-testid="turn-by-service">
          <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-3">Table turn by service</p>
          <div className="flex flex-wrap gap-3">
            {exec.turn_by_service.map((s) => (
              <div key={s.name} data-testid={`turn-service-${s.name}`}
                className="rounded-xl border border-border/60 bg-card px-4 py-3 hover:-translate-y-[1px] transition-transform duration-200">
                <p className="text-sm font-medium">{s.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.avg_turn_minutes}m avg · {s.tables_served} tables</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {summary?.by_source && Object.keys(summary.by_source).length > 0 && (
        <div className="mt-6" data-testid="source-breakdown">
          <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-3">Bookings by source</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.by_source).map(([src, s]) => (
              <div key={src} data-testid={`source-${src}`}
                className="rounded-xl border border-border/60 bg-card px-4 py-3 hover:-translate-y-[1px] transition-transform duration-200">
                <p className="text-sm font-medium capitalize">{src}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.bookings} bookings · {s.covers} covers</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {summary && (
        <p className="text-sm text-muted-foreground mt-6" data-testid="report-avg-party">
          Average party size: <span className="text-foreground font-medium">{summary.avg_party}</span> covers
        </p>
      )}
    </div>
  );
}
